import { createRequestOptions, logFetchError, logFetchFailure } from "../../utils/utils";
import { HttpMethodType } from "../models/HttpMethodType";
import { IDescriptionSuggestionsResult } from "../models/IDescriptionSuggestionsResult";
import { ITimesheetItem } from "../models/ITimesheetItem";
import { ITimesheetSummaryItem } from "../models/ITimesheetSummaryItem";
import { ITimesheetState } from "../models/ITimesheetState";
import { SET_ERROR_MESSAGE } from "./application.actions";

export const timesheetApiUrl: string = "/api/timesheet";

export const SET_ITEMS: string = "SET_ITEMS";
export const SET_SUMMARY_ITEMS: string = "SET_SUMMARY_ITEMS";
export const SET_ISLOADING: string = "SET_ISLOADING";
export const CHANGE_SELECTED_ITEM: string = "CHANGE_SELECTED_ITEM";
export const CLEAR_SELECTED_ITEM: string = "CLEAR_SELECTED_ITEM";
export const DELETE_ITEM_SUCCESS: string = "DELETE_ITEM_SUCCESS";
export const PUT_ITEM_SUCCESS: string = "PUT_ITEM_SUCCESS";
export const SET_DESCRIPTION_SUGGESTIONS: string = "SET_DESCRIPTION_SUGGESTIONS";
export const SET_ISLOADING_DESCRIPTION_SUGGESTIONS: string = "SET_ISLOADING_DESCRIPTION_SUGGESTIONS";
export const SET_SELECTED_START_DATE: string = "SET_SELECTED_START_DATE";

interface ISetItemsActionType { type: string, items: ITimesheetItem[] };
interface ISetSummaryItemsActionType { type: string, summaryItems: ITimesheetSummaryItem[] };
interface ISetIsLoadingActionType { type: string, isLoading: boolean };
interface IChangeSelectedItemActionType { type: string, item: ITimesheetItem };
interface IClearSelectedItemActionType { type: string };
interface IDeleteItemActionType { type: string, item: ITimesheetItem };
interface IPutItemActionType { type: string, item: ITimesheetItem };
interface ISetDescriptionSuggestionsActionType { type: string, descriptionSuggestions: string[] };
interface ISetIsLoadingDescriptionSuggestionsActionType { type: string, isLoadingDescriptionSuggestions: boolean };
interface ISetSelectedStartDateActionType { type: string, selectedStartDate: Date };

export function setItems(items: ITimesheetItem[]): ISetItemsActionType {
    return { type: SET_ITEMS, items: items };
}

export function setSummaryItems(summaryItems: ITimesheetSummaryItem[]): ISetSummaryItemsActionType {
    return { type: SET_SUMMARY_ITEMS, summaryItems: summaryItems };
}

export function setIsLoading(isLoading: boolean): ISetIsLoadingActionType {
    return { type: SET_ISLOADING, isLoading: isLoading };
}

export function changeSelectedItem(item: ITimesheetItem): IChangeSelectedItemActionType {
    return { type: CHANGE_SELECTED_ITEM, item: item };
}

export function clearSelectedItem(): IClearSelectedItemActionType {
    return { type: CLEAR_SELECTED_ITEM };
}

export function deleteItemSuccess(item: ITimesheetItem): IDeleteItemActionType {
    return { type: DELETE_ITEM_SUCCESS, item: item };
}

export function putItemSuccess(item: ITimesheetItem): IPutItemActionType {
    return { type: PUT_ITEM_SUCCESS, item: item };
}

export function setDescriptionSuggestions(descriptionSuggestions: string[]): ISetDescriptionSuggestionsActionType {
    return { type: SET_DESCRIPTION_SUGGESTIONS, descriptionSuggestions: descriptionSuggestions };
}

export function setIsLoadingDescriptionSuggestions(isLoadingDescriptionSuggestions: boolean): ISetIsLoadingDescriptionSuggestionsActionType {
    return { type: SET_ISLOADING_DESCRIPTION_SUGGESTIONS, isLoadingDescriptionSuggestions: isLoadingDescriptionSuggestions };
}

export function setSelectedStartDate(selectedStartDate: Date): ISetSelectedStartDateActionType {
    return { type: SET_SELECTED_START_DATE, selectedStartDate: selectedStartDate };
}

function setGetItemsApplicationError(dispatch: any): void {
    // Update the application error.
    dispatch({ type: SET_ERROR_MESSAGE, errorMessage: "Failed to retrieve items." });

    // Update that items are not loading.
    dispatch({ type: SET_ISLOADING, isLoading: false });
}

function setGetSummaryItemsApplicationError(dispatch: any): void {
    // Update the application error.
    dispatch({ type: SET_ERROR_MESSAGE, errorMessage: "Failed to retrieve summary items." });

    // Update that items are not loading.
    dispatch({ type: SET_ISLOADING, isLoading: false });
}

export function getItems(startDate: Date): any {
    return (dispatch: any, getState: any) => {
        var state: ITimesheetState = getState().timesheetState;
        if (state && state.isLoading) {
            // No need to load as already loading.
            return;
        }

        // Update that items will be loaded.
        dispatch({ type: SET_ISLOADING, isLoading: true });

        const requestInit = createRequestOptions();

        const url = `${timesheetApiUrl}/${startDate.toISOString()}`

        fetch(url, requestInit)
            .then(async (response) => {
                let responseBody = await response.json();

                if (response.ok) {
                    const items = responseBody as ITimesheetItem[];
                    dispatch({ type: SET_ITEMS, items: items });
                }
                else {
                    logFetchFailure(url, response, responseBody);

                    // Update the application error.
                    setGetItemsApplicationError(dispatch);
                }
            })
            .catch((error: any) => {
                logFetchError(error);

                // Update the application error.
                setGetItemsApplicationError(dispatch);
            });
    }
}

export function getSummaryItems(startDate: Date): any {
    return (dispatch: any, getState: any) => {
        var state: ITimesheetState = getState().timesheetState;
        if (state && state.isLoading) {
            // No need to load as already loading.
            return;
        }

        // Update that items will be loaded.
        dispatch({ type: SET_ISLOADING, isLoading: true });

        const requestInit = createRequestOptions();

        const url = `${timesheetApiUrl}/summary/${startDate.toISOString()}`

        fetch(url, requestInit)
            .then(async (response) => {
                let responseBody = await response.json();

                if (response.ok) {
                    const summaryItems = responseBody as ITimesheetSummaryItem[];
                    dispatch({ type: SET_SUMMARY_ITEMS, summaryItems: summaryItems });
                }
                else {
                    logFetchFailure(url, response, responseBody);

                    // Update the application error.
                    setGetSummaryItemsApplicationError(dispatch);
                }
            })
            .catch((error: any) => {
                logFetchError(error);

                // Update the application error.
                setGetSummaryItemsApplicationError(dispatch);
            });
    }
}

export function deleteItem(item: ITimesheetItem): any {
    return (dispatch: any) => {
        const requestInit = createRequestOptions(HttpMethodType.Delete, JSON.stringify(item));
        const deleteUrl = `${timesheetApiUrl}`;
        const errorMessage: string = "Failed to delete item.";

        fetch(deleteUrl, requestInit)
            .then(async (response) => {
                let responseBody = await response.json();

                if (response.ok) {
                    dispatch({ type: DELETE_ITEM_SUCCESS, item: item });
                }
                else {
                    logFetchFailure(deleteUrl, response, responseBody);

                    // Update the application error.
                    dispatch({ type: SET_ERROR_MESSAGE, errorMessage: errorMessage });
                }
            })
            .catch((error: any) => {
                logFetchError(error);

                // Update the application error.
                dispatch({ type: SET_ERROR_MESSAGE, errorMessage: errorMessage });
            });
    }
}

export function upsertItem(item: ITimesheetItem): any {
    return (dispatch: any) => {
        const requestInit = createRequestOptions(HttpMethodType.Put, JSON.stringify(item));
        const upsertUrl = `${timesheetApiUrl}`;
        const errorMessage: string = "Failed to upsert item.";

        fetch(upsertUrl, requestInit)
            .then(async (response) => {
                let responseBody = await response.json();

                if (response.ok) {
                    const upsertedItem = responseBody as ITimesheetItem;
                    dispatch({ type: PUT_ITEM_SUCCESS, item: upsertedItem });
                }
                else {
                    logFetchFailure(upsertUrl, response, responseBody);

                    // Update the application error.
                    dispatch({ type: SET_ERROR_MESSAGE, errorMessage: errorMessage });
                }
            })
            .catch((error: any) => {
                logFetchError(error);

                // Update the application error.
                dispatch({ type: SET_ERROR_MESSAGE, errorMessage: errorMessage });
            });
    }
}

export function getDescriptionSuggestions(): any {
    return (dispatch: any, getState: any) => {
        var state: ITimesheetState = getState().timesheetState;
        if (state && state.isLoadingDescriptionSuggestions) {
            // No need to load as already loading.
            return;
        }

        const errorMessage: string = "Failed to retrieve Description suggestions.";

        // Update that items will be loaded.
        dispatch({ type: SET_ISLOADING_DESCRIPTION_SUGGESTIONS, isLoadingDescriptionSuggestions: true });

        const requestInit = createRequestOptions();
        const url = `${timesheetApiUrl}/descriptionsuggestions`;

        fetch(url, requestInit)
            .then(async (response) => {
                let responseBody = await response.json();

                if (response.ok) {
                    const descriptionSuggestionsResult = responseBody as IDescriptionSuggestionsResult;
                    dispatch({ type: SET_DESCRIPTION_SUGGESTIONS, descriptionSuggestions: descriptionSuggestionsResult.suggestions });
                }
                else {
                    logFetchFailure(url, response, responseBody);

                    // Update the application error.
                    dispatch({ type: SET_ERROR_MESSAGE, errorMessage: errorMessage });
                }
            })
            .catch((error: any) => {
                logFetchError(error);

                // Update the application error.
                dispatch({ type: SET_ERROR_MESSAGE, errorMessage: errorMessage });

                // Update that items are not loading.
                dispatch({ type: SET_ISLOADING_DESCRIPTION_SUGGESTIONS, isLoadingDescriptionSuggestions: false });
            });
    }
}